import { useTranslation } from "react-i18next";
import { FormikPhoneNumber, VehicleType, YesNoAnswer } from "src/types";
import {
  usePhoneNumberSchema,
  ystr,
  ystrRequired,
  ystrRequiredYesNo,
  yupDate,
  yupEmail,
  yupEmailRequired,
  yupInputSelectOption,
  yupTaxCode,
  yupTaxCodeOrVat,
} from "src/utils";
import * as Yup from "yup";

const useVehicleOwnerValidationSchema = () => {
  const phoneSchema = usePhoneNumberSchema({ isRequired: false });
  const { t } = useTranslation();

  const schema = Yup.object().shape({
    ownerCoincidesWithContractor: ystrRequiredYesNo,
    subjectType: ystr.when("ownerCoincidesWithContractor", {
      is: (value: YesNoAnswer) => value === YesNoAnswer.NO,
      then: (schema) => schema.required(t("error.required")),
      otherwise: (schema) => schema.notRequired(),
    }),
    name: ystr.when("ownerCoincidesWithContractor", {
      is: (value: YesNoAnswer) => value === YesNoAnswer.NO,
      then: (schema) => schema.required(t("error.required")),
      otherwise: (schema) => schema.notRequired(),
    }),
    surname: ystr.when("ownerCoincidesWithContractor", {
      is: (value: YesNoAnswer) => value === YesNoAnswer.NO,
      then: (schema) => schema.required(t("error.required")),
      otherwise: (schema) => schema.notRequired(),
    }),
    dateOfBirth: yupDate,
    placeOfBirth: ystr,
    fiscalCodeOrVAT: yupTaxCode,
    address: ystr,
    phoneNumber: phoneSchema.when("ownerCoincidesWithContractor", {
      is: (value: YesNoAnswer) => value === YesNoAnswer.NO,
      then: (schema) => schema.required(t("error.required")),
      otherwise: (schema) => schema.notRequired(),
    }),
    email: ystr.when("ownerCoincidesWithContractor", {
      is: (value: YesNoAnswer) => value === YesNoAnswer.NO,
      then: (schema) => schema.required(t("error.required")),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  return schema;
};

const useVehicleContractorValidationSchema = () => {
  const schema = Yup.object().shape({
    name: ystrRequired,
    surname: ystrRequired,
    fiscalCodeOrVAT: yupTaxCodeOrVat,
    address: ystr,
  });

  return schema;
};

const useVehicleInfoValidationSchema = () => {
  const schema = Yup.object().shape({
    plate: ystrRequired,
    type: ystr,
    registrationStatus: ystr,
  });

  return schema;
};

const useVehicleGreenCardValidationSchema = () => {
  const schema = Yup.object().shape({
    number: ystr,
    startDate: yupDate,
    endDate: yupDate,
  });

  return schema;
};

const useVehicleCompanyValidationSchema = () => {
  const greenCardSchema = useVehicleGreenCardValidationSchema();

  const schema = Yup.object().shape({
    policyNumber: ystr,
    greenCard: greenCardSchema,
    companyName: ystr,
    agencyName: ystr,
    materialDamageCoverage: ystrRequiredYesNo,
  });

  return schema;
};

const useVehicleDriverValidationSchema = ({
  vehicleType,
}: {
  vehicleType: VehicleType;
}) => {
  const { t } = useTranslation();
  const isVehicleA = vehicleType === VehicleType.VEHICLE_A;
  const phoneSchema = usePhoneNumberSchema({ isRequired: isVehicleA });

  const schema = Yup.object().shape({
    driverCoincidesWithContractor: ystrRequiredYesNo,
    name: ystrRequired,
    surname: ystrRequired,
    dateOfBirth: yupDate,
    fiscalCodeOrVAT: yupTaxCode,
    address: ystr,
    phoneNumber: phoneSchema.test(
      "email-or-phone-required-phone",
      t("error.required_email_phone_number"),
      function (value) {
        const { email } = this.parent as { email?: string };

        const emailEmpty = !email || String(email).trim().length === 0;

        const phoneData = value as FormikPhoneNumber;
        const dialCode = phoneData?.country?.dialCode;
        const formattedPhone = phoneData?.formattedPhone;

        const phoneEmpty =
          !formattedPhone ||
          String(formattedPhone).trim().length === 0 ||
          formattedPhone === dialCode;

        if (isVehicleA && phoneEmpty) {
          return this.createError({
            path: `${this.path}.formattedPhone`,
            message: t("error.required"),
          });
        }

        if (!isVehicleA && emailEmpty && phoneEmpty) {
          return this.createError({
            path: `${this.path}.formattedPhone`,
            message: t("error.required_email_phone_number"),
          });
        }

        return true;
      },
    ),
    email: isVehicleA
      ? yupEmailRequired
      : yupEmail.test(
          "email-or-phone-required-email",
          t("error.required_email_phone_number"),
          function (value) {
            const { phoneNumber } = this.parent as {
              phoneNumber?: FormikPhoneNumber;
            };

            const emailEmpty = !value || String(value).trim().length === 0;

            const dialCode = phoneNumber?.country?.dialCode;
            const formattedPhone = phoneNumber?.formattedPhone;

            const phoneEmpty =
              !formattedPhone ||
              String(formattedPhone).trim().length === 0 ||
              formattedPhone === dialCode;

            if (emailEmpty && phoneEmpty) {
              return false;
            }

            return true;
          },
        ),
    licenseNumber: ystr,
    category: yupInputSelectOption,
    validUntil: yupDate,
  });

  return schema;
};

export const useVehicleDamagesValidationSchema = () => {
  const { t } = useTranslation();

  const schema = Yup.object().shape({
    impactPoints: Yup.array().min(1, t("error.at_least_one_impact_point")),
  });

  return schema;
};

export const useVehicleValidationSchema = ({
  vehicleType,
}: {
  vehicleType: VehicleType;
}) => {
  const ownerSchema = useVehicleOwnerValidationSchema();
  const vehilceInfoSchema = useVehicleInfoValidationSchema();
  const contractorSchema = useVehicleContractorValidationSchema();
  const companySchema = useVehicleCompanyValidationSchema();
  const driverSchema = useVehicleDriverValidationSchema({
    vehicleType: vehicleType,
  });
  const damagesSchema = useVehicleDamagesValidationSchema();

  const schema = Yup.object().shape({
    vehicleInfo: vehilceInfoSchema,
    contractor: contractorSchema,
    company: companySchema,
    driver: driverSchema,
    owner: ownerSchema,
    vehicleDamages: damagesSchema,
  });
  return schema;
};
